import {
  actionsAttrs,
  ActionsAttrsType,
  AllAttributesType,
  defaultAttrs,
  DefaultAttrsType,
  formItemsAttrs,
  FormItemsAttrsType,
  formItemScoreAttrs,
  FormItemScoreAttrsType,
  formScoreAttrs,
  FormScoreAttrsType,
  MetricsType,
  negativeResponsesAttrs,
  NegativeResponsesAttrsType,
  scheduleFormsAttrs,
  ScheduleFormsAttrsType,
  submittedFormsAttrs,
  SubmittedFormsAttrsType,
  WidgetDisplayOptions,
} from '@/services/api/resources/analytics-builder/constants';
import { t } from '@/ui/i18n/i18n';
import { ResponsesTypes } from '@/entities/checklist';

export const useAttributes = () => {
  const allAttributeLabels: Record<AllAttributesType, string> = {
    users: t('common.users'),
    templates: t('common.templates'),
    groups: t('common.groups'),
    entities: t('common.entity-items'),
    folders: t('common.entity-folders'),
    categories: t('common.categories'),
    day: t('common.day'),
    week: t('common.week'),
    month: t('common.month'),
    quarter: t('common.quarter'),
    year: t('common.year'),
    punctuality: t('common.punctuality'),
    total: t('common.total'),
    average: t('common.average'),
    'form-item': t('analytics.attribute.form-item'),
    'page-section': t('analytics.attribute.page-section'),
    'action-status': t('analytics.attribute.action-status'),
    creator: t('analytics.attribute.creator'),
    priority: t('common.priority'),
    'form-status': t('analytics.attribute.form-status'),
    response: t('common.response'),
    'approval-status': t('analytics.attribute.approval-status'),
    approvers: t('analytics.attribute.approvers'),
  };

  const allAttributeTypeLabels: Record<AllAttributesType, string> = {
    users: t('common.user'),
    templates: t('common.template'),
    groups: t('common.group'),
    entities: t('common.entity-item'),
    folders: t('common.entity-folder'),
    categories: t('common.category'),
    day: t('common.day'),
    week: t('common.week'),
    month: t('common.month'),
    quarter: t('common.quarter'),
    year: t('common.year'),
    punctuality: t('common.punctuality'),
    total: t('common.total'),
    average: t('common.average'),
    'form-item': t('analytics.attribute.form-item'),
    'page-section': t('analytics.attribute.page-section'),
    'action-status': t('analytics.attribute.action-status'),
    creator: t('analytics.attribute.creator'),
    priority: t('common.priority'),
    'form-status': t('analytics.attribute.form-status'),
    response: t('common.response'),
    'approval-status': t('analytics.attribute.approval-status'),
    approvers: t('analytics.attribute.approvers'),
  };

  const defaultAttrsLabels = (): Array<{value: DefaultAttrsType; label: string}> => [
    { value: 'users', label: allAttributeLabels.users },
    { value: 'templates', label: allAttributeLabels.templates },
    { value: 'groups', label: allAttributeLabels.groups },
    { value: 'entities', label: allAttributeLabels.entities },
    { value: 'folders', label: allAttributeLabels.folders },
    { value: 'categories', label: allAttributeLabels.categories },
    { value: 'day', label: allAttributeLabels.day },
    { value: 'week', label: allAttributeLabels.week },
    { value: 'month', label: allAttributeLabels.month },
    { value: 'quarter', label: allAttributeLabels.quarter },
    { value: 'year', label: allAttributeLabels.year },
  ];

  const submittedFormsAttrsLabels = (): Array<{value: SubmittedFormsAttrsType; label: string}> => [
    ...defaultAttrsLabels(),
    { value: 'punctuality', label: allAttributeLabels.punctuality },
    { value: 'total', label: allAttributeLabels.total },
    { value: 'approval-status', label: allAttributeLabels['approval-status'] },
    { value: 'approvers', label: allAttributeLabels.approvers },
  ];

  const formScoreAttrsLabels = (): Array<{value: FormScoreAttrsType; label: string}> => [
    ...defaultAttrsLabels(),
    { value: 'punctuality', label: allAttributeLabels.punctuality },
    { value: 'average', label: allAttributeLabels.average },
    { value: 'form-item', label: allAttributeLabels['form-item'] },
    { value: 'page-section', label: allAttributeLabels['page-section'] },
    { value: 'approval-status', label: allAttributeLabels['approval-status'] },
    { value: 'approvers', label: allAttributeLabels.approvers },
  ];

  const formItemsAttrsLabels = (): Array<{value: FormItemsAttrsType; label: string}> => [
    ...defaultAttrsLabels(),
    { value: 'punctuality', label: allAttributeLabels.punctuality },
    { value: 'total', label: allAttributeLabels.total },
    { value: 'response', label: allAttributeLabels.response },
    { value: 'approval-status', label: allAttributeLabels['approval-status'] },
    { value: 'approvers', label: allAttributeLabels.approvers },
  ];

  const negativeResponsesAttrsLabels = (): Array<{value: NegativeResponsesAttrsType; label: string}> => [
    ...defaultAttrsLabels(),
    { value: 'punctuality', label: allAttributeLabels.punctuality },
    { value: 'total', label: allAttributeLabels.total },
    { value: 'page-section', label: allAttributeLabels['page-section'] },
    { value: 'form-item', label: allAttributeLabels['form-item'] },
    { value: 'response', label: allAttributeLabels.response },
    { value: 'approval-status', label: allAttributeLabels['approval-status'] },
    { value: 'approvers', label: allAttributeLabels.approvers },
  ];

  const formItemScoreAttrsLabels = (): Array<{value: FormItemScoreAttrsType; label: string}> => [
    ...defaultAttrsLabels(),
    { value: 'punctuality', label: allAttributeLabels.punctuality },
    { value: 'average', label: allAttributeLabels.average },
    { value: 'approval-status', label: allAttributeLabels['approval-status'] },
    { value: 'approvers', label: allAttributeLabels.approvers },
    { value: 'form-item', label: allAttributeLabels['form-item'] },
  ];

  const scheduleFormsAttrsLabels = (): Array<{value: ScheduleFormsAttrsType; label: string}> => [
    ...defaultAttrsLabels(),
    { value: 'form-status', label: allAttributeLabels['form-status'] },
    { value: 'total', label: allAttributeLabels.total },
  ];

  const actionsAttrsLabels = (): Array<{value: ActionsAttrsType; label: string}> => [
    ...defaultAttrsLabels(),
    { value: 'punctuality', label: allAttributeLabels.punctuality },
    { value: 'total', label: allAttributeLabels.total },
    { value: 'action-status', label: allAttributeLabels['action-status'] },
    { value: 'creator', label: allAttributeLabels.creator },
    { value: 'priority', label: allAttributeLabels.priority },
    { value: 'form-item', label: allAttributeLabels['form-item'] },
    { value: 'page-section', label: allAttributeLabels['page-section'] },
    { value: 'approval-status', label: allAttributeLabels['approval-status'] },
    { value: 'approvers', label: allAttributeLabels.approvers },
  ];

  const getAttributesByMetricWithLabels = (metric: MetricsType | null) => {
    switch (metric) {
      case 'submitted-forms':
        return submittedFormsAttrsLabels();
      case 'form-score':
        return formScoreAttrsLabels();
      case 'form-items':
        return formItemsAttrsLabels();
      case 'negative-responses':
        return negativeResponsesAttrsLabels();
      case 'form-item-score':
        return formItemScoreAttrsLabels();
      case 'schedule-forms':
        return scheduleFormsAttrsLabels();
      case 'actions':
        return actionsAttrsLabels();
      default:
        return defaultAttrsLabels();
    }
  };

  const getAttributesByMetric = (metric: MetricsType): Array<unknown> => {
    switch (metric) {
      case 'submitted-forms':
        return submittedFormsAttrs();
      case 'form-score':
        return formScoreAttrs();
      case 'form-items':
        return formItemsAttrs();
      case 'negative-responses':
        return negativeResponsesAttrs();
      case 'form-item-score':
        return formItemScoreAttrs();
      case 'schedule-forms':
        return scheduleFormsAttrs();
      case 'actions':
        return actionsAttrs();
      default:
        return defaultAttrs();
    }
  };

  const getDisplayByResponseType = (type: ResponsesTypes) => {
    const displays = allowedDisplaysByResponseType[type];

    return displays ? displays[0] : null;
  };

  const allowedDisplaysByResponseType: Record<string, WidgetDisplayOptions[]> = {
    [ResponsesTypes.list]: [WidgetDisplayOptions.barChart, WidgetDisplayOptions.table],
    [ResponsesTypes.checkbox]: [WidgetDisplayOptions.barChart, WidgetDisplayOptions.table],
    [ResponsesTypes.dropdown]: [WidgetDisplayOptions.barChart, WidgetDisplayOptions.table],
    [ResponsesTypes.number]: [WidgetDisplayOptions.lineChart, WidgetDisplayOptions.table],
    [ResponsesTypes.text]: [WidgetDisplayOptions.table],
    [ResponsesTypes.datetime]: [WidgetDisplayOptions.table],
    [ResponsesTypes.qrCode]: [WidgetDisplayOptions.table],
    [ResponsesTypes.geoLocation]: [WidgetDisplayOptions.table],
    [ResponsesTypes.entityItem]: [WidgetDisplayOptions.table],
    [ResponsesTypes.annotation]: [WidgetDisplayOptions.table],
    [ResponsesTypes.signature]: [WidgetDisplayOptions.table],
    [ResponsesTypes.photo]: [WidgetDisplayOptions.table],
    [ResponsesTypes.file]: [WidgetDisplayOptions.table],
  };

  const getAllowedDisplaysByResponseType = (type: ResponsesTypes) => allowedDisplaysByResponseType[type] || [];

  return {
    allAttributeLabels,
    allAttributeTypeLabels,
    getAttributesByMetric,
    getAttributesByMetricWithLabels,
    getDisplayByResponseType,
    allowedDisplaysByResponseType,
    getAllowedDisplaysByResponseType,
  };
};
